import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Receipt, ShieldCheck, ShieldAlert, PlugZap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getBillingSettings, saveBillingSettings, testBillingConnection } from "@/lib/billing";

const EMPTY = {
  name: "", cif: "", email: "",
  api_token: "",
  is_vat_payer: true, vat_rate: 19,
  series_factura: "", series_proforma: "", series_aviz: "", series_storno: "", series_incasare: "",
  default_document_type: "factura",
  auto_emit_on_order: false, auto_emit_on_paid: false, auto_emit_on_fulfilled: false,
  auto_mark_paid_after_reconciliation: false,
  include_shipping_as_item: true, shipping_product_name: "Transport",
};

export default function BillingSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [masked, setMasked] = useState(null);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    getBillingSettings().then((d) => {
      setConfigured(d.configured);
      if (d.company) {
        const c = d.company;
        setForm({
          name: c.name || "", cif: c.cif || "", email: c.email || "", api_token: "",
          is_vat_payer: c.is_vat_payer ?? true, vat_rate: c.vat_rate ?? 19,
          series_factura: c.series_factura || "", series_proforma: c.series_proforma || "",
          series_aviz: c.series_aviz || "", series_storno: c.series_storno || "",
          series_incasare: c.series_incasare || "",
          default_document_type: c.default_document_type || "factura",
          auto_emit_on_order: c.auto_emit_on_order || false,
          auto_emit_on_paid: c.auto_emit_on_paid || false,
          auto_emit_on_fulfilled: c.auto_emit_on_fulfilled || false,
          auto_mark_paid_after_reconciliation: c.auto_mark_paid_after_reconciliation || false,
          include_shipping_as_item: c.include_shipping_as_item ?? true,
          shipping_product_name: c.shipping_product_name || "Transport",
        });
        setMasked(c.api_token_masked);
      }
    }).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.cif || !form.email) { toast.error("CIF + email obligatorii"); return; }
    if (!configured && !form.api_token) { toast.error("API Token obligatoriu la prima setare"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.api_token) delete payload.api_token;
      const r = await saveBillingSettings(payload);
      setConfigured(true);
      setMasked(r.company.api_token_masked);
      setForm((s) => ({ ...s, api_token: "" }));
      toast.success("Setări salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testBillingConnection();
      setTestResult(r);
      if (r.ok) toast.success("Conexiune SmartBill OK");
      else toast.error(r.error || "Conexiune eșuată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la testare");
    } finally { setTesting(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Se încarcă…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="billing-settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Setări</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Receipt className="h-7 w-7 text-primary" /> Facturare · SmartBill
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Conectează contul SmartBill Cloud. Tokenul este criptat la nivel de bază de date.
        </p>
      </div>

      <Card className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-base">Conexiune API</h2>
          {configured ? (
            <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Configurat</Badge>
          ) : (
            <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">Neconfigurat</Badge>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Denumire companie</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="IEFTINIT.RO SRL" data-testid="bs-name" />
          </div>
          <div className="space-y-1.5">
            <Label>CIF *</Label>
            <Input value={form.cif} onChange={(e) => set("cif", e.target.value.toUpperCase())} placeholder="RO12345678" data-testid="bs-cif" />
          </div>
          <div className="space-y-1.5">
            <Label>Email SmartBill *</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="cont@email.ro" data-testid="bs-email" />
          </div>
          <div className="space-y-1.5">
            <Label>API Token {configured && <span className="text-xs text-muted-foreground">(actual: {masked})</span>}</Label>
            <Input type="password" value={form.api_token} onChange={(e) => set("api_token", e.target.value)} placeholder={configured ? "Lasă gol pentru a păstra tokenul" : "API token din SmartBill"} data-testid="bs-token" />
            <div className="text-[11px] text-muted-foreground">Îl găsești în SmartBill → Setări → Integrări → API. Necesită permisiuni admin.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>Plătitor TVA</Label>
            <div className="flex items-center gap-2 h-9">
              <Switch checked={form.is_vat_payer} onCheckedChange={(v) => set("is_vat_payer", v)} data-testid="bs-vat-payer" />
              <span className="text-sm">{form.is_vat_payer ? "Da" : "Nu"}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Cotă TVA standard (%)</Label>
            <Input type="number" step="0.5" value={form.vat_rate} onChange={(e) => set("vat_rate", parseFloat(e.target.value) || 0)} data-testid="bs-vat-rate" />
          </div>
          <div className="space-y-1.5">
            <Label>Tip document default</Label>
            <Select value={form.default_document_type} onValueChange={(v) => set("default_document_type", v)}>
              <SelectTrigger data-testid="bs-default-doc"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="factura">Factură</SelectItem>
                <SelectItem value="proforma">Proformă</SelectItem>
                <SelectItem value="aviz">Aviz</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <Button onClick={save} disabled={saving} data-testid="bs-save-btn">{saving ? "Se salvează…" : "Salvează setări"}</Button>
          <Button variant="outline" onClick={runTest} disabled={testing || !configured} data-testid="bs-test-btn">
            {testing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PlugZap className="h-3.5 w-3.5 mr-1" />}
            Testează conexiunea
          </Button>
        </div>
        {testResult && (
          <div className={`rounded-lg border p-3 text-sm ${testResult.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="bs-test-result">
            <div className="flex items-center gap-2 font-medium">
              {testResult.ok ? <ShieldCheck className="h-4 w-4 text-emerald-700" /> : <ShieldAlert className="h-4 w-4 text-rose-700" />}
              {testResult.ok ? "Conexiune funcțională" : "Conexiune eșuată"}
            </div>
            {testResult.ok ? (
              <div className="mt-2 text-xs space-y-1 text-emerald-900">
                <div>Companie: <span className="font-mono">{testResult.company?.name || form.name} · {testResult.company?.cif}</span></div>
                <div>Cote TVA SmartBill: <span className="font-mono">{(testResult.taxes || []).slice(0, 6).map(t => `${t.percentage}%`).join(", ") || "—"}</span></div>
                <div>Serii detectate: <span className="font-mono">{(testResult.series || []).slice(0, 6).map(s => s.name).join(", ") || "—"}</span></div>
              </div>
            ) : (
              <div className="mt-2 text-xs text-rose-900">
                <div>{testResult.error}</div>
                {testResult.status_code && <div className="text-rose-700/70">HTTP {testResult.status_code}</div>}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-6 space-y-5">
        <h2 className="font-semibold text-base">Serii documente</h2>
        <p className="text-xs text-muted-foreground -mt-3">Configurează seriile SmartBill pentru fiecare tip. Lasă gol dacă vrei să folosești seria default a contului.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Serie factură</Label>
            <Input value={form.series_factura} onChange={(e) => set("series_factura", e.target.value)} placeholder="FACT" data-testid="bs-series-factura" />
          </div>
          <div className="space-y-1.5">
            <Label>Serie proformă</Label>
            <Input value={form.series_proforma} onChange={(e) => set("series_proforma", e.target.value)} placeholder="PROF" data-testid="bs-series-proforma" />
          </div>
          <div className="space-y-1.5">
            <Label>Serie aviz</Label>
            <Input value={form.series_aviz} onChange={(e) => set("series_aviz", e.target.value)} placeholder="AVZ" data-testid="bs-series-aviz" />
          </div>
          <div className="space-y-1.5">
            <Label>Serie storno</Label>
            <Input value={form.series_storno} onChange={(e) => set("series_storno", e.target.value)} placeholder="STR" data-testid="bs-series-storno" />
          </div>
          <div className="space-y-1.5">
            <Label>Serie încasare (chitanță)</Label>
            <Input value={form.series_incasare} onChange={(e) => set("series_incasare", e.target.value)} placeholder="CHT" data-testid="bs-series-incasare" />
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-base">Automatizări</h2>
        <p className="text-xs text-muted-foreground -mt-3">Emitere factură + înregistrare încasare automată în SmartBill.</p>
        <div className="space-y-3">
          <Row label="Emite automat la comandă nouă" value={form.auto_emit_on_order} onChange={(v) => set("auto_emit_on_order", v)} testid="bs-auto-order" />
          <Row label="Emite la marcare ca plătită" value={form.auto_emit_on_paid} onChange={(v) => set("auto_emit_on_paid", v)} testid="bs-auto-paid" />
          <Row label="Emite la fulfillment (expediere)" value={form.auto_emit_on_fulfilled} onChange={(v) => set("auto_emit_on_fulfilled", v)} testid="bs-auto-fulfilled" />
          <Row label="Marchează automat factura ca plătită după reconciliere GLS COD" value={form.auto_mark_paid_after_reconciliation} onChange={(v) => set("auto_mark_paid_after_reconciliation", v)} testid="bs-auto-mark-paid" />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h2 className="font-semibold text-base">Transport pe factură</h2>
        <Row label="Include costul de transport ca linie separată" value={form.include_shipping_as_item} onChange={(v) => set("include_shipping_as_item", v)} testid="bs-include-shipping" />
        <div className="space-y-1.5">
          <Label>Denumire produs transport</Label>
          <Input value={form.shipping_product_name} onChange={(e) => set("shipping_product_name", e.target.value)} data-testid="bs-shipping-name" />
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border-b border-border last:border-0 pb-3 last:pb-0">
      <div className="text-sm">{label}</div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
